import { pgEnum } from "drizzle-orm/pg-core";

export const assignmentRole = pgEnum("assignment_role", ["R1", "R2", "R3", "R4", "Supporting"]);

export const podComposition = pgEnum("pod_composition", ["Solo", "Pair", "Trio", "Quad"]);

export const solutionStatus = pgEnum("solution_status", ["Active", "Pending", "Closed"]);

// Pipeline Report — fixed, code-governed vocabulary (not admin-configurable, unlike
// lead_source, an RM/ADMIN pick list — Stage itself is free-form text, see
// pipeline-report-entries.ts). Whether a pipeline_report_entry's Demand currently
// has a Resource covering it.
export const resourcingStatus = pgEnum("resourcing_status", [
  "Staffed",
  "PartiallyStaffed",
  "NotStaffed",
]);

// ── Staffing Requests (docs/specs/staffing-requests.md) ──────────────────────
//
// A request to change one Solution's Pod: Add a Resource (open seat, a new seat, or
// Supporting), Replace one, or Remove one. [ACCT]/[SA] request; [RM] decides.

export const staffingRequestKind = pgEnum("staffing_request_kind", [
  "Add",
  "Replace",
  "Remove",
  "Swap",
]);

export const staffingRequestStatus = pgEnum("staffing_request_status", [
  "Pending",
  "Approved",
  "Rejected",
  "Withdrawn",
]);

// ── Signals (docs/specs/suggested-intensity-from-guidance.md) ────────────────
//
// RM triage layer for Suggested-Intensity-from-Guidance. processSolution raises,
// updates, or auto-resolves a signal; it never writes to Intensity or Pod.

export const signalType = pgEnum("signal_type", [
  "needs_guidance",
  "guidance_change_pod_change",
  "intensity_drift",
  // A Resource has NOS guidance and/or logged actual hours on the Solution's
  // Demands but holds no active assignment on its Pod — suggest adding them.
  // Unlike the three types above (one open per Solution), this is deduped per
  // (Solution, Resource): a Solution may have several off-Pod contributors.
  "resource_off_pod",
]);

export const signalStatus = pgEnum("signal_status", [
  "open",
  "confirmed",
  "dismissed",
  "resolved_by_system",
]);

// ── Resource Plan (docs/specs/resource-plan.md, M2 = docs/specs/resource-plan-m2-handoff.md) ──
//
// Every value of every enum below is declared here, in one CREATE TYPE, even though M2
// only drives a subset. Postgres refuses to use a value added by ALTER TYPE ADD VALUE
// inside the transaction that added it (55P04), and src/db/migrate.ts wraps each
// migration file in a single transaction — so a later slice that adds "SoftCommitted"
// and indexes on it in the same migration would fail. Declaring the full vocabulary now
// costs nothing and keeps M5 a table change rather than a two-migration dance.

/**
 * The plan lifecycle (§4.6). M2 implements Draft → Proposed → Won → Lost only; the
 * transition map in src/lib/resource-plan-lifecycle.ts is the authority on what is
 * legal, and deliberately OMITS the later statuses rather than stubbing them.
 *
 * `Staffed` is retired (2026-10-14) — the plan's Resourcing status says it — but stays in
 * the type: frozen versions and history recorded it, and they are append-only. No live
 * plan holds it (see the retire_staffed_plan_status migration).
 */
export const resourcePlanStatus = pgEnum("resource_plan_status", [
  "Draft",
  "Proposed",
  "SoftCommitted",
  "Won",
  "HandedOver",
  "Staffed",
  "Active",
  "Closed",
  "Lost",
  "Superseded",
  // Added 2026-10-14: a plan nobody may change until its status is changed back.
  "Locked",
]);

/**
 * A Solution may hold many plans but at most one Active Plan (RP-1.1). A second plan on
 * a Solution that already has one must say which of these it is.
 */
export const planKind = pgEnum("plan_kind", ["Base", "Option", "ChangeOrder"]);

/** RP-1.1. Determines whether a Contracted Fee is expected (§4.4) — never whether revenue is derived. */
export const pricingModel = pgEnum("pricing_model", ["TimeAndMaterials", "FixedPrice"]);

/** RP-1.1. Monthly is a run-rate with an optional end; Total is an envelope. */
export const budgetCadence = pgEnum("budget_cadence", ["Monthly", "Total"]);

/**
 * How hours are entered (RP-4.4).
 *
 * `SteadyState` — ONE hours-per-month figure per Plan Line, expanded across the plan's
 * months for every total. A Monthly plan is a run-rate: typing the same number into 12 or
 * 36 identical columns is data entry the shape of the plan should have spared you.
 * `plan_line_month` holds nothing in this mode; the months are derived.
 *
 * `MonthByMonth` — a value per cell, which is what a Total-cadence envelope needs.
 *
 * Defaulted from the cadence at creation (Monthly → SteadyState, Total → MonthByMonth)
 * and switchable: going to MonthByMonth MATERIALISES the steady-state figure into every
 * month so nothing is lost, per RP-4.4's "copies the steady-state hours into every month
 * for editing".
 *
 * NOTE ON SCOPE: RP-4.4 was deferred to M3 in the M2 handoff. It was pulled forward at
 * the product owner's request — a Monthly plan that forces 36 identical columns is the
 * thing they actually objected to.
 */
export const planEntryMode = pgEnum("plan_entry_mode", ["MonthByMonth", "SteadyState"]);

/**
 * A Resource Manager's own staffing flag on a Resource Plan — where the RM is in
 * resourcing the plan, INDEPENDENT of its sales/lifecycle `resource_plan_status`. A plan
 * can be Proposed and already Staffing, or Won and still InReview. `Returned` means the
 * RM rejected the plan back to its author; the lifecycle status is left alone.
 *
 * Not to be confused with `resourcing_status` above, which is the Pipeline Report's
 * per-Demand coverage vocabulary. NULL on the column means "Not set".
 */
export const planResourcingStatus = pgEnum("plan_resourcing_status", [
  "InReview",
  "Staffing",
  "Staffed",
  // Set only by the Reject action (it carries a reason) — never picked from the control.
  "Returned",
]);

/**
 * What kind of staffing problem a Staffing Concern raises (RP-34.2). Fixed vocabulary — the
 * message carries the detail.
 */
export const planConcernType = pgEnum("plan_concern_type", [
  "NoAvailableResource",
  "SkillGap",
  "StartDate",
  "OverBudgetForStaffing",
  "Other",
]);

/**
 * Where a Staffing Concern stands (RP-34). Only a Blocking Concern passes through
 * Acknowledged — the account side's "we have seen it" that lets the plan be Staffed.
 */
export const planConcernStatus = pgEnum("plan_concern_status", [
  "Open",
  "Acknowledged",
  "Resolved",
]);

/**
 * Six types, resolved RP-D4. The first four are exactly the L1 Domain Model components
 * so a plan and a solution plan describe the same engagement in the same words; the last
 * two carry work that is not build work and would distort the build templates.
 */
export const workstreamType = pgEnum("workstream_type", [
  "Experience",
  "Workflow",
  "Integration",
  "Foundation",
  "Advisory",
  "RunSupport",
]);

/**
 * RP-6.3. `Card` prices every line from the resolved Rate Basis; `ClientDictated` takes a
 * bill rate per line with no Rate Card lineage (government and framework schedules).
 * The plan still resolves a Rate Basis in BOTH modes — basis is a property of the
 * engagement (RP-12.3) and the margin floor comes from it.
 */
export const planRateMode = pgEnum("plan_rate_mode", ["Card", "ClientDictated"]);

/**
 * RP-6.1's labelling AC as data: the source of each applied rate. `AccountOverride` is a
 * negotiated Account rate (RP-12.4) resolved ahead of the Rate Card — see
 * src/api/plan-rates.ts for the resolution and src/db/schema/account-rate-overrides.ts for
 * the table it reads.
 */
export const planRateSource = pgEnum("plan_rate_source", [
  "Card",
  "AccountOverride",
  "LineOverride",
  "ClientDictated",
]);

/**
 * RP-32.1 — why a Suggested Resource was named. "Client Requested" is the one the RM most
 * needs to see: swapping that person is a conversation with the client, not a staffing call.
 */
export const suggestedResourceReason = pgEnum("suggested_resource_reason", [
  "ClientRequested",
  "PitchedToClient",
  "Continuity",
  "Other",
]);

/**
 * RP-32.2 / 32.3 — why a Plan Line Candidate left the shortlist. Removed: the RM took them
 * off. Promoted: they became the Suggested Resource. ClearedOnPromote: another Candidate
 * was promoted. ClearedOnStaffed: the plan's Resourcing status was set to Staffed.
 * LineRemoved: the line itself went away.
 */
export const planLineCandidateOutcome = pgEnum("plan_line_candidate_outcome", [
  "Removed",
  "Promoted",
  "ClearedOnPromote",
  "ClearedOnStaffed",
  "LineRemoved",
]);

/** RP-1.2. Fixed Price + Total captures a Total fee; Fixed Price + Monthly a Monthly one. */
export const contractedFeeBasis = pgEnum("contracted_fee_basis", ["Total", "Monthly"]);

// ── Resource Plan M3 — Level 1 estimating and the Planned Pod ─────────────────
//
// Same discipline as the M2 block above: every value declared in one CREATE TYPE, so a
// later slice can index on a value in the migration that introduces it. See the note at
// the head of the M2 block for why (55P04 + the single-transaction migration runner).

/**
 * RP-2.1 / RP-2.2 / RP-2.3. Where a plan's Planned Pod came from, and the input for
 * M7's Shape Adherence report (RP-23.1).
 *
 * `Template` — every Position is exactly as the chosen Pod Shape specifies.
 * `Modified` — started from a Shape, then a Position was added, removed, or
 *   re-disciplined. The plan keeps its `shape_id` so the deviation list means something.
 * `Custom`   — built from nothing. `shape_id` is NULL; the nearest Shape is COMPUTED for
 *   display (RP-2.3) and never stored, because the Shape library moves underneath it.
 *
 * NULL on `resource_plan.shape_source` means "no Planned Pod composed yet" — a plan can
 * be priced without one, and M2 plans all have none. It is not a fourth state.
 *
 * Shapes are MEASURED, NEVER ENFORCED (non-negotiable 7). Nothing in this codebase may
 * block, penalise, or nag a Custom Planned Pod: the moment it does, planners pick the
 * nearest Shape and lie by omission, and the adherence data is worthless.
 */
export const shapeSource = pgEnum("shape_source", ["Template", "Modified", "Custom"]);

/**
 * RP-3.2 / RP-13.1. The Size presets a Workstream can be sized by, mapping to default
 * hours per Workstream Type in the Template.
 *
 * Recorded on the Workstream so "sized at M" survives the entry, and so re-sizing after
 * a mix override can re-apply the same preset against the overridden mix (RP-3.3).
 */
export const workstreamSizePreset = pgEnum("workstream_size_preset", ["S", "M", "L", "XL"]);

// ── Resource Plan M4 — Governance (Epics 8, 17, 10) ──────────────────────────
//
// Same discipline as the M2/M3 blocks: every value in one CREATE TYPE, so the migration
// that introduces an enum can also index on one of its values (55P04 + the
// single-transaction migration runner — see the note at the head of the M2 block).

/**
 * Why a version was frozen (RP-8.1: "freeze a version on Sent, Approved, and Won").
 *
 * `Sent`     — Draft → Proposed on a plan that needed no approval.
 * `Approved` — a [FIN] approver cleared a below-floor plan; the freeze and the
 *              transition to Proposed happen together (RP-17.2's workflow step).
 * `Won`      — the signed shape, frozen with its Won date and SOW reference.
 *
 * All three are per `version_number`, not instead of it: one version can carry a Sent
 * snapshot and later a Won one. Nothing is editable between those two freezes (only a
 * Draft is editable), so they differ only in the lifecycle fields — which is exactly the
 * distinction worth keeping, because the Won snapshot is the version of record for the
 * signed deal.
 *
 * `Committed` — a planner's own checkpoint of a plan that is Won or later, taken with
 *               "Commit version". Unlike the three above it is not tied to a lifecycle
 *               edge: after Won the plan's content stays editable, and a commit is how a
 *               planner marks "this is the shape now". Each commit takes its own
 *               `version_number`, so the (plan, number, kind) unique index still holds.
 */
export const planVersionKind = pgEnum("plan_version_kind", [
  "Sent",
  "Approved",
  "Won",
  "Committed",
]);

/**
 * The state of an Approval Request (RP-17.1 … RP-17.3).
 *
 * `Pending` holds the plan in Draft (Awaiting Approval) — RP-9.1 is explicit that the
 * floor "never blocks the plan, it blocks this transition pending a decision", which is
 * why there is no eleventh `resource_plan_status`. The plan stays Draft and this row is
 * what makes the header say "Awaiting Approval".
 */
export const planApprovalStatus = pgEnum("plan_approval_status", [
  "Pending",
  "Approved",
  "Rejected",
  // Closed without a decision (2026-10-14): the plan moved on, or the figures it asked
  // about changed and a fresh request replaced it. Set by [SYSTEM] paths only.
  "Withdrawn",
]);

/**
 * What raised an Approval Request (2026-10-14).
 *
 *   • `Propose` — the Draft → Proposed gate (RP-17.1). Approving freezes an Approved
 *                 version and moves the plan to Proposed.
 *   • `Edit`    — an edit to a plan that had already been sent took it below the floor
 *                 or a line below cost. Approving only records the decision; the plan's
 *                 status never moves (an edit never changes status).
 */
export const planApprovalOrigin = pgEnum("plan_approval_origin", ["Propose", "Edit"]);

/**
 * RP-28 — how a plan's dates were changed (`plan_date_change.mode`).
 *
 *   • `Move` — every month's hours slid by N months; nothing else changed.
 *   • `Fit`  — the hours were fitted to a new window: Total cadence keeps each Plan Line's
 *              total, Monthly cadence keeps the run-rate.
 */
export const planDateChangeMode = pgEnum("plan_date_change_mode", ["Move", "Fit"]);

/**
 * Which Rate Card a new plan on an Account starts on (Set tier and default rate card,
 * specs/workspaces/feat-set-tier-and-default-rate-card-t9re.md). `List` prices every line
 * from the firm-wide Rate Card; `Account` takes the Account's negotiated rates first
 * (RP-12.4), list for the rest. A default only — the planner still picks per plan, and the
 * choice is frozen on `resource_plan.use_account_rate_card`.
 */
export const accountDefaultRateSource = pgEnum("account_default_rate_source", ["List", "Account"]);
